import { useCallback } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useFormError } from 'contexts/FormErrorProvider';
import type { RecordSection, TabWithSections } from 'types';
import { getTabsWithFormError } from '../../tabs';
import { cloneDeep } from 'lodash';
import type { RegisteredFormWithRole, Snapshots } from 'contexts/FormRegistryProvider';
import { scrollToError } from '../../formErrorScroll';

interface UseRecordErrorHandlingProps {
  tabs: TabWithSections[];
  sectionRefs: React.MutableRefObject<Record<string, { expand: () => void }>>;
}

export const useRecordErrorHandling = ({ tabs, sectionRefs }: UseRecordErrorHandlingProps) => {
  const { dispatch } = useFormError();

  const handleOnError = useCallback(
    (errors: FieldErrors, nestedListForms?: RegisteredFormWithRole[], snapshots?: Snapshots) => {
      const errorFieldIds = Object.keys(errors);

      nestedListForms?.forEach((form) => {
        const nestedFormsErrors = form.api.getErrors?.();

        if (nestedFormsErrors && Object.keys(nestedFormsErrors).length > 0) {
          errorFieldIds.push(form.id);
        }
      });

      snapshots?.forEach((snapshot) => {
        const snapshotErrors = snapshot.errors;

        if (snapshotErrors && Object.keys(snapshotErrors).length > 0 && snapshot.meta?.formItemId !== undefined) {
          errorFieldIds.push(snapshot.meta.formItemId);
        }
      });

      const tabsWithErrors = getTabsWithFormError(tabs, errorFieldIds);

      const sectionsWithErrors = tabs.reduce<string[]>((acc, tab) => {
        const errorSections = tab.sections
          .filter((section: RecordSection) => section.formItems.some((item) => errorFieldIds.includes(item.formItem.id)))
          .map((section: RecordSection) => section.id);

        return [...acc, ...errorSections];
      }, []);

      dispatch({ type: 'SET_TABS_WITH_ERRORS', payload: cloneDeep(tabsWithErrors) });

      setTimeout(() => {
        sectionsWithErrors.forEach((sectionId) => {
          sectionRefs.current[sectionId]?.expand();
        });
        scrollToError(errorFieldIds, {
          offset: 200,
        });
      }, 100);
    },
    [dispatch, sectionRefs, tabs]
  );

  return {
    handleOnError,
  };
};
